/**
 * ClassCardOverlayBadge — inline status badge for class cards.
 *
 * Priority (high → low):
 *   1. Class status: CANCELED
 *   2. User's own registration: REGISTERED (green) / UNPAID (orange)
 *   3. Class status: WAITLIST / SOLD OUT
 *
 * The user-registration branch requires the viewer to have an active
 * registration that isn't on the waitlist; waitlisted users fall through to
 * the class-level WAITLIST badge.
 */

/* eslint-disable react-refresh/only-export-components */

import { CheckCircle2, Clock4, XCircle, Users, Ban } from "lucide-react";
import type { ClassStatus } from "@/lib/api/classes";

export type OverlayBadgeKind =
  | "registered"
  | "unpaid"
  | "canceled"
  | "waitlist"
  | "sold_out";

interface BadgeConfig {
  icon: typeof XCircle;
  label: string;
  /** Background tint used on the inline badge (renders on dark card surfaces). */
  bgClass: string;
}

const BADGE_CONFIG: Record<OverlayBadgeKind, BadgeConfig> = {
  registered: { icon: CheckCircle2, label: "REGISTERED", bgClass: "bg-green-500/40" },
  unpaid: { icon: Clock4, label: "UNPAID", bgClass: "bg-orange-500/40" },
  waitlist: { icon: Users, label: "WAITLIST", bgClass: "bg-yellow-500/40" },
  sold_out: { icon: XCircle, label: "SOLD OUT", bgClass: "bg-red-500/40" },
  canceled: { icon: Ban, label: "CANCELED", bgClass: "bg-red-500/40" },
};

/** Pick the right badge to show, applying the priority rules described above. */
export function pickOverlayBadge(
  status: ClassStatus,
  userRegistration?: { isPaid: boolean; isOnWaitlist: boolean } | null,
): OverlayBadgeKind | null {
  if (status === "canceled") return "canceled";
  if (userRegistration && !userRegistration.isOnWaitlist) {
    return userRegistration.isPaid ? "registered" : "unpaid";
  }
  if (status === "waitlist") return "waitlist";
  if (status === "sold_out") return "sold_out";
  return null;
}

/**
 * @deprecated Prefer `pickOverlayBadge`; this only considers class status and
 * misses the user's own registration state.
 */
export function shouldShowOverlayBadge(
  status: ClassStatus,
): status is "waitlist" | "sold_out" | "canceled" {
  return status === "waitlist" || status === "sold_out" || status === "canceled";
}

interface ClassCardOverlayBadgeProps {
  /** New API: pass the badge kind directly. */
  kind?: OverlayBadgeKind;
  /** Legacy API: pass the class status; kept for existing callers. */
  status?: "waitlist" | "sold_out" | "canceled";
}

export function ClassCardOverlayBadge({
  kind,
  status,
}: ClassCardOverlayBadgeProps) {
  const resolved: OverlayBadgeKind | null = kind ?? status ?? null;
  if (!resolved) return null;
  const { icon: Icon, label, bgClass } = BADGE_CONFIG[resolved];

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md ${bgClass} text-white`}
    >
      <Icon className="h-3 w-3" />
      <span className="font-mono text-[10px] font-semibold tracking-wider">
        {label}
      </span>
    </span>
  );
}
